<script lang="ts">
    import { getContext } from 'svelte'
    import { flip } from 'svelte/animate'
    import type { Writable } from 'svelte/store'

    import Icon from '@iconify/svelte'

    import type { HydriaDocument } from 'hydria/HydriaDocument'
    import Modal from 'hydria/components/Modal.svelte'
    import { createDragHandle } from 'hydria/external/DragHandleZone'
    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { type SimpleFetchResult, simpleFetch } from 'hydria/services/session'

    import SchemeSelection from '../BaseFields/Scheme.svelte'
    import VerticalFieldWidget from '../Widgets/VerticalFieldWidget.svelte'
    import type { IHomeNewsListSection, ISectionedRevision } from '../models'
    import SchemedHtmlField from './Fields/SchemedHtmlField.svelte'
    import { asAny } from 'hydria/svelte_helpers'

    interface Props {
        sectionEdit: IHomeNewsListSection
        sectionSaved: IHomeNewsListSection
        disabled: boolean
        set?: (v) => void
    }

    let { sectionEdit = $bindable(), sectionSaved, disabled, set: doSet = (v) => {} }: Props = $props()

    const revEdit = getContext('revEdit') as Writable<ISectionedRevision>
    const Document = getContext('Document') as HydriaDocument

    let docListReady = false
    interface IDoc {
        id: string
        title: string
    }
    let documents: IDoc[] = $state([])
    let docIndex: { [key: string]: IDoc } = $state({})

    async function loadDocList() {
        let r: SimpleFetchResult
        try {
            r = await simpleFetch('GET', '/json/cms/1/nouvelles', { limit: 200 })
        } catch (e: any) {
            r = e
        }

        if (!r || !r.data || !r.data.success) {
            notify({
                title: tgettext('Chargement des nouvelles'),
                text: tgettext('Les nouvelles n’ont pas pu être chargé.'),
                type: 'danger',
            })
            documents = []
            docIndex = {}
        } else {
            documents = r.data.items
                .filter((inDoc: any) => inDoc.lgs[$revEdit.lg] != null && inDoc.lgs[$revEdit.lg].online != null && inDoc.lgs[$revEdit.lg].online.active)
                .map((inDoc: any) => {
                    const o = inDoc.lgs[$revEdit.lg].online
                    return {
                        id: inDoc.id,
                        title: o.title,
                    }
                })

            docIndex = Object.fromEntries(documents.map((s) => [s.id, s]))
        }

        docListReady = true
    }
    loadDocList()

    // ****************************************************************************  Styles / Schemes
    function findStyle(source: string[], start: string) {
        const first = source.filter((s) => s.startsWith(`${start}-`))[0]
        return first != null ? first.slice(start.length + 1) : null
    }
    function setStyle(start: string, value: string) {
        const data = { styles: [...sectionEdit.styles.filter((s) => !s.startsWith(`${start}-`)), `${start}-${value}`] }
        data.styles.sort()
        doSet(data)
    }

    let schemeClass: string | null = $derived(sectionEdit != null ? findStyle(sectionEdit.styles, 'scheme') : null)
    let savedSchemeClass: string | null = $derived(sectionSaved != null ? findStyle(sectionSaved.styles, 'scheme') : null)

    // ****************************************************************************  Ajout / Enlever

    // null = no modal, Set<string> = what to add, modal open
    let addDocModal: Set<string> | null = $state(null)
    let possibleDocs: IDoc[] = $derived(documents.filter((s) => !sectionEdit.doc_ids.includes(s.id)))

    // ****************************************************************************  Sort
    const flipDurationMs = 250

    function handleSort(e: any) {
        // sectionEdit.doc_ids = e.detail.items.map((item) => item.id)
        sortDocIds = e.detail.items
    }

    function handleSortEnd(e: any) {
        sortDocIds = e.detail.items
        sectionEdit.doc_ids = e.detail.items.map((item) => item.id)
        doSet({ doc_ids: sectionEdit.doc_ids })
    }

    const { dragHandleZone, dragHandle } = createDragHandle()

    // La lib de dragging a *besoin* d'un element "id".
    // Donc on crée une liste artificielle.
    let sortDocIds: { id: string }[] = $derived(
        sectionEdit.doc_ids.map((id) => {
            return { id: id }
        }),
    )
</script>

<style>
    .add-doc-list {
        columns: 15em;
        gap: 1em;
    }
</style>

<div class="d-flex gap-1 mb-3">
    <div class="col" style="max-width: 14rem;">
        <SchemeSelection {disabled} value={schemeClass} saved={savedSchemeClass} save={(e) => setStyle('scheme', e)} />
    </div>
    <div class="col"></div>
    <div class="col" style="max-width: 12rem;">
        <VerticalFieldWidget label={tgettext('Ajouter des documents')} isValid={true} isPristine={true}>
            <button type="button" class="btn btn-primary" {disabled} onclick={() => (addDocModal = new Set())}><Icon inline icon="mdi:playlist-plus" /> {$_('Ajouter des documents')}</button>
        </VerticalFieldWidget>
    </div>
</div>

{#if sectionEdit}
    <SchemedHtmlField
        value={sectionEdit.intro_html}
        saved={sectionSaved ? sectionSaved.intro_html : undefined}
        {Document}
        styles={sectionEdit.styles}
        label={tgettext('Texte d’introduction')}
        helpText={tgettext('Ne pas oublier d’ajouter un titre H1 pour nommer la section !')}
        {disabled}
        setValue={(e) => {
            doSet({ intro_html: e })
        }}
    />
{/if}

<div class="entries d-grid gap-3 mt-3" use:dragHandleZone={{ items: sortDocIds, flipDurationMs: flipDurationMs, type: 'nouvelle' }} onconsider={handleSort} onfinalize={handleSortEnd}>
    {#each sortDocIds as sdoc (sdoc.id)}
        <div class="card overflow-hidden" animate:flip={{ duration: flipDurationMs }}>
            <div class="card-body text-bg-light d-flex align-items-center gap-2">
                <div><span use:dragHandle class="btn btn-sm btn-outline-secondary"><Icon inline icon="mdi:drag-vertical" /></span></div>
                <div class:text-danger={!docIndex[sdoc.id]}>{docIndex[sdoc.id] ? docIndex[sdoc.id].title : '? ' + $_('Document inconnu')}</div>
                <div class="col"></div>
                <div>
                    <button
                        type="button"
                        class="btn btn-sm btn-danger"
                        {disabled}
                        onclick={() => {
                            doSet({
                                doc_ids: sectionEdit.doc_ids.filter((s) => s != sdoc.id),
                            })
                        }}><Icon inline icon="mdi:trash" /></button
                    >
                </div>
            </div>
        </div>
    {/each}
    {#if !sortDocIds.length}
        <div class="card overflow-hidden">
            <div class="card-body text-bg-light text-center text-muted"><button type="button" class="btn btn-warning" {disabled} onclick={() => (addDocModal = new Set())}>{$_('Aucun document.')}</button></div>
        </div>
    {/if}
</div>

{#if addDocModal != null}
    <Modal close={() => (addDocModal = null)}>
        <div class="modal-header">
            <h5>{$_('Ajout de documents')}</h5>
        </div>
        <div class="modal-body">
            <div class="add-doc-list">
                {#each possibleDocs as doc (doc.id)}
                    <div
                        aria-hidden="true"
                        onclick={(e) => {
                            e.preventDefault()
                            if (asAny(addDocModal).has(doc.id)) asAny(addDocModal).delete(doc.id)
                            else asAny(addDocModal).add(doc.id)

                            // Trigger reactivity
                            addDocModal = addDocModal
                        }}
                    >
                        <Icon inline icon={addDocModal.has(doc.id) ? 'mdi:checkbox-marked' : 'mdi:checkbox-blank-outline'} />
                        <span>{doc.title}</span>
                    </div>
                {/each}
            </div>
        </div>
        <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" onclick={() => (addDocModal = null)}>{$_('Fermer')}</button>
            <button
                type="button"
                class="btn btn-primary"
                onclick={() => {
                    doSet({
                        doc_ids: sectionEdit.doc_ids.concat(Array.from(asAny(addDocModal))),
                    })
                    addDocModal = null
                }}
                disabled={addDocModal.size == 0}>{$_('Ajouter')}</button
            >
        </div>
    </Modal>
{/if}
